import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BookDemoModal from '@/pages/home/components/BookDemoModal';

const caseStudies = [
  {
    tag: 'HVAC',
    headline: 'HVAC Company — Increased Bookings by 37%',
    problem: 'Missed calls and delayed responses were costing this HVAC operator jobs every single day during peak season.',
    solution: 'AI call handling + instant booking system deployed across all inbound channels.',
    results: [{ stat: '37%', label: 'Increase in booked jobs' }, { stat: '60%', label: 'Faster response time' }, { stat: '↓ Admin', label: 'Reduced workload' }],
    quote: 'We went from missing 3–4 calls a day to capturing every single one. The difference in revenue was immediate.',
    author: 'Operations Director, HVAC Co.',
    image: 'https://public.readdy.ai/ai/img_res/89b9a920b1d610fc609d096196901e6d.jpg',
    accent: '#F97316',
  },
  {
    tag: 'Plumbing',
    headline: 'Plumbing Business — Captured Lost Revenue',
    problem: 'High volume of missed emergency calls meant customers were calling competitors instead.',
    solution: '24/7 automated response system with instant lead capture and routing.',
    results: [{ stat: '100%', label: 'Call coverage achieved' }, { stat: '+28%', label: 'Conversion rate increase' }, { stat: '30d', label: 'Time to positive ROI' }],
    quote: 'Emergency calls used to go to voicemail after hours. Now every call gets an instant response and we book the job.',
    author: 'Owner, Metro Plumbing Services',
    image: 'https://readdy.ai/api/search-image?query=professional%20plumber%20carrying%20tools%20walking%20toward%20residential%20building%2C%20service%20truck%20in%20background%2C%20urban%20street%2C%20bright%20natural%20daylight%2C%20commercial%20photography%2C%20photorealistic&width=900&height=600&seq=cs-plumbing-apple-01&orientation=landscape',
    accent: '#06B6D4',
  },
  {
    tag: 'Roofing',
    headline: 'Roofing Contractor — Improved Lead Conversion',
    problem: 'Estimates were going out but follow-up was inconsistent, leaving deals on the table.',
    solution: 'Automated follow-up sequences with intelligent lead qualification workflows.',
    results: [{ stat: '+44%', label: 'Higher close rate' }, { stat: '−5d', label: 'Shortened sales cycle' }, { stat: '+$18k', label: 'Average deal value increase' }],
    quote: 'We were leaving money on the table with every estimate we sent. The automated follow-up changed everything.',
    author: 'CEO, Summit Roofing Group',
    image: 'https://readdy.ai/api/search-image?query=roofing%20contractor%20team%20on%20residential%20roof%20with%20clear%20blue%20sky%20background%2C%20professional%20workers%20in%20safety%20gear%2C%20wide%20angle%20shot%2C%20warm%20sunlight%2C%20commercial%20photography%2C%20photorealistic&width=900&height=600&seq=cs-roofing-apple-01&orientation=landscape',
    accent: '#2563EB',
  },
];

const tagColors: Record<string, { bg: string; text: string; border: string }> = {
  HVAC: { bg: '#FEF3C7', text: '#F97316', border: '#F97316' },
  Plumbing: { bg: '#ECFEFF', text: '#06B6D4', border: '#06B6D4' },
  Roofing: { bg: '#EFF6FF', text: '#2563EB', border: '#2563EB' },
};

export default function CaseStudiesPage() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#060A14] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=home%20service%20business%20owner%20reviewing%20growth%20charts%20on%20laptop%20in%20modern%20office%2C%20confident%20expression%2C%20clean%20minimal%20workspace%2C%20warm%20lighting%2C%20commercial%20photography%2C%20photorealistic&width=1600&height=700&seq=cs-hero-apple-01&orientation=landscape"
            alt="Case Studies"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/60 via-[#060A14]/50 to-[#060A14]"></div>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-[#2563EB]/12 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#06B6D4] text-xs font-semibold tracking-[0.2em] uppercase mb-6 font-inter">Real Outcomes, Real Businesses</p>
          <h1 className="text-6xl md:text-7xl font-black text-white leading-[1.0] tracking-tight mb-6 font-poppins">Case Studies</h1>
          <p className="text-white/45 text-xl leading-relaxed font-inter font-light">Real outcomes from real service businesses using PureHomeScale systems.</p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-6">
          {caseStudies.map((cs, i) => {
            const colors = tagColors[cs.tag] || { bg: '#F8FAFC', text: '#334155', border: '#E2E8F0' };
            return (
              <div key={i} className="bg-white rounded-3xl overflow-hidden border border-[#E2E8F0]/80">
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${i % 2 !== 0 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                  {/* Image */}
                  <div className="relative h-72 lg:h-auto min-h-[360px] overflow-hidden">
                    <img src={cs.image} alt={cs.headline} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent"></div>
                    <div className="absolute top-6 left-6">
                      <span
                        className="text-xs font-bold px-4 py-1.5 rounded-full font-inter"
                        style={{ backgroundColor: `${colors.bg}`, color: colors.text, border: `1px solid ${colors.border}30` }}
                      >
                        {cs.tag}
                      </span>
                    </div>
                    {/* Stats overlay on mobile */}
                    <div className="absolute bottom-6 left-6 right-6 lg:hidden flex gap-2 flex-wrap">
                      {cs.results.map((r, ri) => (
                        <div key={ri} className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2">
                          <div className="font-black text-lg leading-none font-poppins" style={{ color: cs.accent }}>{r.stat}</div>
                          <div className="text-[#334155]/60 text-xs mt-0.5 font-inter">{r.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-10 md:p-12 flex flex-col justify-between">
                    <div>
                      <h2 className="text-[#0F172A] font-black text-2xl md:text-3xl mb-6 leading-tight font-poppins">{cs.headline}</h2>
                      <div className="space-y-5 mb-8">
                        <div>
                          <span className="text-[#F97316] text-[10px] font-bold uppercase tracking-[0.15em] font-inter">Problem</span>
                          <p className="text-[#334155]/55 text-sm mt-1.5 leading-relaxed font-inter">{cs.problem}</p>
                        </div>
                        <div>
                          <span className="text-[#22C55E] text-[10px] font-bold uppercase tracking-[0.15em] font-inter">Solution</span>
                          <p className="text-[#334155]/55 text-sm mt-1.5 leading-relaxed font-inter">{cs.solution}</p>
                        </div>
                      </div>

                      {/* Stats — desktop */}
                      <div className="hidden lg:flex gap-3 mb-8">
                        {cs.results.map((r, ri) => (
                          <div key={ri} className="bg-[#F8FAFC] rounded-2xl px-5 py-4 flex-1 border border-[#E2E8F0]/80">
                            <div className="font-black text-2xl leading-none font-poppins mb-1" style={{ color: cs.accent }}>{r.stat}</div>
                            <div className="text-[#334155]/45 text-xs font-inter">{r.label}</div>
                          </div>
                        ))}
                      </div>

                      <blockquote className="border-l-2 pl-5" style={{ borderColor: `${cs.accent}40` }}>
                        <p className="text-[#334155]/65 text-sm italic leading-relaxed mb-2 font-inter">&ldquo;{cs.quote}&rdquo;</p>
                        <cite className="text-[#334155]/35 text-xs not-italic font-inter">— {cs.author}</cite>
                      </blockquote>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#2563EB]/12 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#F97316] text-xs font-semibold tracking-[0.2em] uppercase mb-6 font-inter">Your Turn</p>
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 font-poppins leading-tight">Your Business Could Be Next</h2>
          <p className="text-white/40 text-lg mb-10 max-w-xl mx-auto font-inter font-light">
            See what&apos;s possible when every lead is captured, every call is answered, and every estimate gets followed up.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="group bg-white text-[#0F172A] font-semibold text-base px-10 py-4 rounded-full hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap font-inter inline-flex items-center gap-2.5"
          >
            See What&apos;s Possible
            <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200"></i>
          </button>
        </div>
      </section>

      <Footer />
      <BookDemoModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
